import { useGetRecordBySlugQuery, useGetRecordQuery } from "@/api/api";
import { useParams } from "react-router";
import PageContainer from "./PageContainer";
import RecordView from "./RecordView";
import { deserializeRecord } from "@/common/record";
import { useDocumentTitle } from "@/common/useDocumentTitle";
import RecordSheet from "./RecordSheet";
import { Button } from "../button";
import { Skeleton } from "../skeleton";
import { Edit } from "lucide-react";
import { useCanEditRecord, useCanonicalPath } from "@/api/hooks";
import { isExternalRecordId } from "@/providers/external/adapt";

function Message({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full items-center justify-center">
      <p className="text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

/**
 * The page's own shape, drawn before its content arrives.
 *
 * A record is fetched by id or slug, so there is nothing to show until the
 * round trip lands — but the shape is known, and laying it out now means the
 * text drops into place rather than shoving a centred word aside. Mirrors
 * `RecordView`'s `showTitle` block: heading, interval line, rule, body.
 */
function RecordSkeleton() {
  return (
    <div className="flex h-full w-full flex-col gap-3" aria-hidden>
      <div className="flex shrink-0 flex-col gap-3">
        <Skeleton className="h-[29px] w-[62%]" />
        <Skeleton className="h-[12px] w-[170px]" />
        {/* The rule is not content: it is the same 40x2 mark the record itself
            draws, so it does not move when the record lands. */}
        <div className="h-[2px] w-[40px] bg-primary" />
      </div>
      <div className="flex flex-col gap-2 pt-2">
        {["100%", "96%", "88%", "0", "93%", "70%"].map((width, i) =>
          width === "0" ? (
            <div key={i} className="h-2" />
          ) : (
            <Skeleton key={i} className="h-[15px]" style={{ width }} />
          ),
        )}
      </div>
    </div>
  );
}

/**
 * Serves both record URLs:
 *   /{user}/-/{slug}   the readable one
 *   /record/{id}       the legacy permalink, and the only form a record
 *                      without a slug has
 */
export default function RecordPage() {
  const { recordID, userID, recordSlug } = useParams<{
    recordID: string;
    userID: string;
    recordSlug: string;
  }>();

  const bySlug = useGetRecordBySlugQuery(
    { userRef: userID || "", recordRef: recordSlug || "" },
    { skip: !userID || !recordSlug },
  );
  // An "ext:" id belongs to a record fetched live from an external system;
  // there is no row to ask for, and asking would 404.
  const isExternal = isExternalRecordId(recordID);
  const byID = useGetRecordQuery(recordID || "", {
    skip: !recordID || isExternal,
  });

  const recordRequest = recordID ? byID : bySlug;

  // Set when the URL used a stale slug, or an id for a record that has a
  // slug. Hooks must run before the early returns below.
  useCanonicalPath(recordRequest.data?.canonical_path);
  // Before the early returns below -- hooks cannot run conditionally.
  const canEdit = useCanEditRecord(recordRequest.data);
  // The tab names the record. Undefined while the fetch is out (and on an id
  // that resolves to nothing), which leaves the app name up rather than
  // flashing an empty title.
  useDocumentTitle(recordRequest.data?.title);

  const record = deserializeRecord(recordRequest.data ?? null);

  // Every state is wrapped in the same container, so the page keeps its width
  // and padding throughout and the record does not jump on arrival.
  let body: React.ReactNode;
  if (!recordID && !recordSlug) {
    // Unreachable through the router, which matches one or the other.
    body = <Message>No record</Message>;
  } else if (isExternal) {
    // Reached by hand or from a stale tab: external records are shown in place
    // on the timeline and open outwards, so there is nothing to render here
    // beyond saying where they live.
    body = (
      <Message>
        This record lives in an external system and has no page here. Open the
        collection it belongs to and it will be listed in place.
      </Message>
    );
  } else if (recordRequest.isLoading) {
    body = <RecordSkeleton />;
  } else if (recordRequest.isError) {
    body = <Message>This record could not be loaded.</Message>;
  } else if (!record) {
    body = <Message>Record not found.</Message>;
  } else {
    body = (
      <>
        <RecordView record={record} showTitle />
        {canEdit && (
          <div className="flex justify-end gap-2">
            <RecordSheet record={record} title="Edit record">
              <Button variant="ghost">
                <Edit />
              </Button>
            </RecordSheet>
          </div>
        )}
      </>
    );
  }

  return <PageContainer>{body}</PageContainer>;
}
